/**
 * Estimate Builder — create or edit a field estimate.
 *
 * URL params:
 *   /estimates/new        → fresh draft
 *   /estimates/:id        → load existing
 *
 * Sections (vertical accordion-style):
 *   1. Customer / prospect picker
 *   2. Scope (title, description, job address)
 *   3. Line items (labour, materials, vehicle, fuel, equipment, disposal, custom)
 *   4. Tax + deposit
 *   5. Sticky totals + actions
 *
 * Everything is computed client-side for live preview, then re-computed
 * server-side on save so workers can't tamper with prices.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CustomColumnInputs } from '../components/ColumnLayoutEditor';
import { EditableHeader } from '../components/DailyInvoiceBits';
import {
  Save, Send, Plus, Trash2, Loader2, ArrowLeft, Hammer, Package,
  Truck, Fuel, Wrench, Recycle, Receipt, Calculator, FileText, Download,
} from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EMPTY = {
  title: '', description: '', job_address: '',
  customer_id: null, prospect: { name: '', email: '', phone: '', address: '' },
  labour: [], materials: [], vehicle: [], fuel: [], equipment: [], disposal: [], custom: [],
  tax_preset_id: null, tax_rate_override: 13, material_markup_pct: null,
  deposit_required: false, deposit_pct: 25,
  internal_notes: '',
  presentation: 'itemized', summary_included: '', currency: '',
};

function SectionCard({ title, icon: Icon, description, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Icon className="w-4 h-4 text-blue-500" /> {title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export default function EstimateBuilder() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { token, user, isAdmin, isSupervisor } = useAuth();
  const { toast } = useToast();
  const canSend = isAdmin || isSupervisor;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [data, setData] = useState({ ...EMPTY });
  const [serverDoc, setServerDoc] = useState(null);  // existing estimate from server

  // Load company estimate settings + customer list + catalogue + (if editing) the estimate
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, c, m] = await Promise.all([
          axios.get(`${API}/estimate-settings`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/material-catalog`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (cancelled) return;
        setSettings(s.data);
        setCustomers((c.data || []).filter(x => !x.deleted_at));
        setCatalogue(m.data?.items || []);
        if (!isNew) {
          const r = await axios.get(`${API}/estimates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          if (cancelled) return;
          setServerDoc(r.data);
          // Mint stable React keys onto every row when loading from API
          // (server payloads don't carry `_id`). Without this, removing a
          // middle row would shuffle inputs onto the wrong line. We use a
          // closure-captured uid generator that mirrors the one inside the
          // component body.
          const _bfUid = () => `ln-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
          const _key = (arr) => (arr || []).map((row) => row?._id ? row : { ...row, _id: _bfUid() });
          setData({
            title: r.data.title || '',
            description: r.data.description || '',
            job_address: r.data.job_address || '',
            customer_id: r.data.customer_id || null,
            prospect: r.data.prospect || { name: '', email: '', phone: '', address: '' },
            labour: _key(r.data.labour),
            materials: _key(r.data.materials),
            vehicle: _key(r.data.vehicle),
            fuel: _key(r.data.fuel),
            equipment: _key(r.data.equipment),
            disposal: _key(r.data.disposal),
            custom: _key(r.data.custom),
            tax_preset_id: r.data.tax_preset_id || null,
            tax_rate_override: r.data.tax_rate_override ?? 13,
            material_markup_pct: r.data.material_markup_pct ?? null,
            deposit_required: !!r.data.deposit_required,
            deposit_pct: r.data.deposit_pct ?? 25,
            internal_notes: r.data.internal_notes || '',
            presentation: r.data.presentation || 'itemized',
            summary_included: r.data.summary_included || '',
            currency: r.data.currency || '',
          });
          setColLabels(Array.isArray(r.data.columns) && r.data.columns.length ? Object.fromEntries(r.data.columns.map((c) => [c.key, c.label])) : {});
        } else if (s.data.deposit_enabled) {
          setData(d => ({ ...d, deposit_required: false, deposit_pct: s.data.deposit_pct }));
        }
      } catch (e) {
        toast({ title: 'Failed to load estimate', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew, token]);

  // ─── Live totals (mirror backend _compute_line_totals) ─────────────────
  const totals = useMemo(() => {
    const num = (v) => Number(v) || 0;
    const labour_total = (data.labour || []).reduce((s, l) => s + num(l.hours) * num(l.rate), 0);
    const mat_default = num(data.material_markup_pct);
    const materials_total = (data.materials || []).reduce((s, m) => {
      const mk = m.markup_pct == null || m.markup_pct === '' ? mat_default : num(m.markup_pct);
      return s + num(m.quantity) * num(m.unit_cost) * (1 + mk / 100);
    }, 0);
    const vehicle_total = (data.vehicle || []).reduce((s, v) => {
      if (v.mode === 'distance') return s + num(v.distance) * num(v.rate_per_unit);
      return s + num(v.flat_amount);
    }, 0);
    const fuel_total = (data.fuel || []).reduce((s, f) => {
      if (f.mode === 'percent_of_labour') return s + labour_total * num(f.percent) / 100;
      return s + num(f.flat_amount);
    }, 0);
    const equipment_total = (data.equipment || []).reduce((s, e) => s + num(e.days) * num(e.daily_rate), 0);
    const disposal_total = (data.disposal || []).reduce((s, d) => s + num(d.amount), 0);
    const custom_total = (data.custom || []).reduce((s, c) => s + num(c.quantity || 1) * num(c.rate), 0);
    const subtotal = labour_total + materials_total + vehicle_total + fuel_total + equipment_total + disposal_total + custom_total;
    const tax_rate = num(data.tax_rate_override);
    const tax_total = tax_rate > 0 ? subtotal * tax_rate / 100 : 0;
    const grand_total = subtotal + tax_total;
    const deposit_amount = data.deposit_required ? grand_total * num(data.deposit_pct) / 100 : 0;
    return {
      labour_total, materials_total, vehicle_total, fuel_total, equipment_total, disposal_total,
      custom_total, subtotal, tax_total, grand_total, deposit_amount,
    };
  }, [data]);

  // Auto-enforce deposit when threshold is exceeded
  useEffect(() => {
    if (!settings?.deposit_enabled) return;
    const threshold = settings.deposit_required_above;
    if (threshold != null && totals.grand_total >= threshold && !data.deposit_required) {
      setData(d => ({ ...d, deposit_required: true, deposit_pct: d.deposit_pct || settings.deposit_pct }));
    }
  }, [totals.grand_total, settings, data.deposit_required]);

  // ─── Helpers for each line-item type ───────────────────────────────────
  // Stable per-row React key. Using array index as the key (the old approach)
  // caused inputs to "swap" their typed values into the wrong row whenever a
  // user removed a line in the middle of the list — React reused the DOM
  // nodes by position. `_uid()` mints a unique non-persisted id once per
  // row so React keeps each <Input> bound to the right state slot.
  const _uid = () => `ln-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const _ensureId = (row) => (row && row._id ? row : { ...row, _id: _uid() });
  const addLabour = () => {
    const def = (settings?.labour_roles || [])[0];
    setData(d => ({ ...d, labour: [...d.labour, _ensureId({ role: def?.name || 'Technician', hours: 1, rate: def?.rate_per_hour || 0, description: '' })] }));
  };
  const addMaterial = () => setData(d => ({ ...d, materials: [...d.materials, _ensureId({ description: '', quantity: 1, unit_cost: 0, markup_pct: null, unit_label: 'ea' })] }));
  const addVehicle = () => setData(d => ({ ...d, vehicle: [...d.vehicle, _ensureId({
    mode: 'distance', distance: 0, distance_unit: settings?.vehicle_unit || 'km',
    rate_per_unit: settings?.vehicle_rate_per_unit || 0.85, flat_amount: 0, description: '',
  })] }));
  const addFuel = () => setData(d => ({ ...d, fuel: [...d.fuel, _ensureId({ mode: 'percent_of_labour', percent: 5, flat_amount: 0, description: 'Fuel surcharge' })] }));
  const addEquipment = () => setData(d => ({ ...d, equipment: [...d.equipment, _ensureId({ description: '', days: 1, daily_rate: 0 })] }));
  const addDisposal = () => setData(d => ({ ...d, disposal: [...d.disposal, _ensureId({ description: 'Disposal / dump fee', amount: 0 })] }));
  const addCustom = () => setData(d => ({ ...d, custom: [...d.custom, _ensureId({ description: '', quantity: 1, rate: 0 })] }));

  const [baseColumns, setBaseColumns] = useState([]);
  // Per-estimate header renames (printed on the PDF/XLSX) — {column key: label}. Sep 2026.
  const [colLabels, setColLabels] = useState({});
  const docColumns = baseColumns.map((c) => (colLabels[c.key] ? { ...c, label: colLabels[c.key] } : c));
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/document-columns/resolve`, { params: { doc: 'estimate', customer_id: data.customer_id || undefined }, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBaseColumns(r.data.columns || [])).catch(() => setBaseColumns([]));
  }, [token, data.customer_id]); // eslint-disable-line
  const hasCustomCols = docColumns.some((c) => c.source === 'custom');
  const customRow = (key, idx, row) => (hasCustomCols ? (
    <div className="col-span-12"><CustomColumnInputs columns={docColumns} values={row.custom_fields} onChange={(v) => updateLine(key, idx, { custom_fields: v })} testIdPrefix={`${key}-cf-${idx}`} compact /></div>
  ) : null);
  const updateLine = (key, idx, patch) => setData(d => ({
    ...d,
    [key]: d[key].map((row, i) => i === idx ? { ...row, ...patch } : row),
  }));
  const removeLine = (key, idx) => setData(d => ({
    ...d,
    [key]: d[key].filter((_, i) => i !== idx),
  }));

  const handleSave = async (statusAfter = null) => {
    if (!data.title.trim()) {
      toast({ title: 'Title required', description: 'Give the estimate a short name.', variant: 'destructive' });
      return;
    }
    if (!data.customer_id && !(data.prospect?.name && data.prospect?.email)) {
      toast({ title: 'Pick a customer or fill the prospect form', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: data.title.trim(),
        description: data.description,
        job_address: data.job_address,
        customer_id: data.customer_id || undefined,
        prospect: data.customer_id ? undefined : data.prospect,
        labour: data.labour, materials: data.materials, vehicle: data.vehicle,
        fuel: data.fuel, equipment: data.equipment, disposal: data.disposal, custom: data.custom,
        tax_preset_id: data.tax_preset_id,
        tax_rate_override: data.tax_rate_override,
        material_markup_pct: data.material_markup_pct,
        deposit_required: data.deposit_required,
        deposit_pct: data.deposit_pct,
        internal_notes: data.internal_notes,
        presentation: data.presentation,
        summary_included: data.summary_included || null,
        currency: data.currency || null,
        columns: Object.keys(colLabels).length ? docColumns : null,
      };
      let res;
      if (isNew) {
        res = await axios.post(`${API}/estimates`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Draft created', description: `Saved as ${res.data.estimate_number}` });
        if (statusAfter !== 'send') {
          navigate(`/estimates/${res.data.id}`, { replace: true });
        }
      } else {
        res = await axios.patch(`${API}/estimates/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setServerDoc(res.data);
        toast({ title: 'Saved' });
      }
      return res.data;
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!canSend) {
      toast({ title: 'Only admins / supervisors can send estimates', description: 'Save as a draft — an admin will review and send.', variant: 'destructive' });
      return;
    }
    const saved = await handleSave('send');
    const targetId = saved?.id || id;
    if (!targetId) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/estimates/${targetId}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.emailed) {
        toast({ title: 'Estimate sent', description: `Email + PDF delivered to ${res.data.to_email}` });
      } else {
        toast({
          title: 'Estimate marked as sent',
          description: 'Delivery is being processed. If the customer doesn\'t receive it shortly, copy the public link from the list view and share it directly — our team has been notified automatically.',
        });
      }
      navigate('/estimates');
    } catch (e) {
      toast({ title: 'Send failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`${API}/estimates/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${serverDoc?.estimate_number || 'estimate'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  const customerObj = customers.find(c => c.id === data.customer_id);
  const roles = settings?.labour_roles || [];
  const vehicleUnit = settings?.vehicle_unit || 'km';
  const readOnly = serverDoc && ['accepted', 'converted'].includes(serverDoc.status);

  return (
    <div className="p-6 space-y-6 pb-32" data-testid="estimate-builder">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate('/estimates')} className="mb-2"><ArrowLeft className="w-4 h-4 mr-2" /> Back to estimates</Button>
          <h1 className="text-2xl font-bold text-white" data-testid="estimate-title-heading">
            {isNew ? 'New estimate' : serverDoc?.estimate_number}
          </h1>
          {serverDoc && (
            <Badge className="mt-1 capitalize" data-testid="estimate-status-badge">{serverDoc.status}</Badge>
          )}
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
          )}
          <Button onClick={() => handleSave()} disabled={saving || readOnly} variant="outline" data-testid="save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save draft
          </Button>
          {canSend && !readOnly && (
            <Button onClick={handleSend} disabled={sending || saving} data-testid="send-estimate-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Save &amp; Send
            </Button>
          )}
        </div>
      </div>

      {/* ─── Customer / Prospect ───────────────────────────────────────── */}
      <SectionCard title="Customer or prospect" icon={Receipt} description="Pick an existing customer, or capture a new prospect. Accepted estimates auto-promote prospects to customers." testId="section-customer">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Existing customer</Label>
            <Select
              value={data.customer_id || 'none'}
              onValueChange={(v) => setData(d => ({ ...d, customer_id: v === 'none' ? null : v }))}
              disabled={readOnly}
            >
              <SelectTrigger data-testid="customer-select"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None (new prospect) —</SelectItem>
                {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ''}</SelectItem>))}
              </SelectContent>
            </Select>
            {customerObj && <p className="text-xs text-slate-500 mt-2">{customerObj.email || ''} {customerObj.address ? `· ${customerObj.address}` : ''}</p>}
          </div>
          {!data.customer_id && (
            <div className="space-y-3" data-testid="prospect-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prospect name</Label><Input value={data.prospect.name} onChange={(e) => setData(d => ({ ...d, prospect: { ...d.prospect, name: e.target.value } }))} data-testid="prospect-name" /></div>
                <div><Label>Email</Label><Input type="email" value={data.prospect.email} onChange={(e) => setData(d => ({ ...d, prospect: { ...d.prospect, email: e.target.value } }))} data-testid="prospect-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={data.prospect.phone} onChange={(e) => setData(d => ({ ...d, prospect: { ...d.prospect, phone: e.target.value } }))} data-testid="prospect-phone" /></div>
                <div><Label>Address</Label><Input value={data.prospect.address} onChange={(e) => setData(d => ({ ...d, prospect: { ...d.prospect, address: e.target.value } }))} data-testid="prospect-address" /></div>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* ─── Scope ─────────────────────────────────────────────────────── */}
      <SectionCard title="Scope" icon={FileText} description="What the customer is getting quoted for." testId="section-scope">
        <div className="space-y-3">
          <div>
            <Label>Job title</Label>
            <Input value={data.title} onChange={(e) => setData(d => ({ ...d, title: e.target.value }))} placeholder="e.g. Drywall repair — basement" data-testid="estimate-title" disabled={readOnly} />
          </div>
          <div>
            <Label>Scope of work</Label>
            <Textarea rows={4} value={data.description} onChange={(e) => setData(d => ({ ...d, description: e.target.value }))} placeholder="Plain-English description the customer will read on the PDF." data-testid="estimate-description" disabled={readOnly} />
          </div>
          <div>
            <Label>Job site address</Label>
            <Input value={data.job_address} onChange={(e) => setData(d => ({ ...d, job_address: e.target.value }))} placeholder="Where the work will be performed" data-testid="estimate-job-address" disabled={readOnly} />
          </div>
        </div>
      </SectionCard>

      {/* ─── Labour ────────────────────────────────────────────────────── */}
      <SectionCard title="Column headers" icon={Hammer} description="As printed on this estimate's PDF and XLSX. Click a header to rename it for this estimate only." testId="section-columns">
        <div className="flex flex-wrap items-center gap-2">
          {docColumns.map((c) => (
            <div key={c.key} className={`px-2.5 py-1.5 rounded-md text-[11px] uppercase tracking-wide font-semibold border ${colLabels[c.key] ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300'}`} style={{ minWidth: 90 }} data-testid={`estimate-colchip-${c.key}`}>
              <EditableHeader col={c} testPrefix="estimate-col" onRename={(key, label) => setColLabels((m) => ({ ...m, [key]: label }))} />
            </div>
          ))}
          {Object.keys(colLabels).length > 0 && (
            <button type="button" onClick={() => setColLabels({})} className="text-xs text-sky-600 dark:text-sky-400 hover:underline ml-2" data-testid="estimate-columns-reset">Reset to customer/company headers</button>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Labour" icon={Hammer} description="Hours per role. Rates can be edited inline; defaults come from Settings → Estimates." testId="section-labour">
        {(data.labour || []).map((l, idx) => (
          <div key={l._id || `labour-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`labour-row-${idx}`}>
            <div className="col-span-4">
              <Label className="text-xs">Role</Label>
              <Select value={l.role} onValueChange={(v) => {
                const r = roles.find(x => x.name === v);
                updateLine('labour', idx, { role: v, rate: r ? r.rate_per_hour : l.rate });
              }}>
                <SelectTrigger data-testid={`labour-role-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map(r => (<SelectItem key={r.id || r.name} value={r.name}>{r.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Hours</Label><Input type="number" min={0} step={0.25} value={l.hours} onChange={(e) => updateLine('labour', idx, { hours: parseFloat(e.target.value || '0') })} data-testid={`labour-hours-${idx}`} /></div>
            <div className="col-span-2"><Label className="text-xs">$/hr</Label><Input type="number" min={0} step={0.5} value={l.rate} onChange={(e) => updateLine('labour', idx, { rate: parseFloat(e.target.value || '0') })} data-testid={`labour-rate-${idx}`} /></div>
            <div className="col-span-3"><Label className="text-xs">Notes</Label><Input value={l.description || ''} onChange={(e) => updateLine('labour', idx, { description: e.target.value })} placeholder="Optional" data-testid={`labour-notes-${idx}`} /></div>
            <div className="col-span-1 flex justify-end">
              <Button size="icon" variant="ghost" onClick={() => removeLine('labour', idx)} data-testid={`labour-remove-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {customRow('labour', idx, l)}
            <div className="col-span-12 text-right text-xs text-slate-400">= {fmtMoney(Number(l.hours || 0) * Number(l.rate || 0))}</div>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addLabour} disabled={readOnly} data-testid="add-labour-btn"><Plus className="w-4 h-4 mr-1" /> Add labour line</Button>
      </SectionCard>

      {/* ─── Materials ─────────────────────────────────────────────────── */}
      <SectionCard title="Materials" icon={Package} description="Pick from the company catalogue or add ad-hoc. Markup applies on top of cost." testId="section-materials">
        {(data.materials || []).map((m, idx) => (
          <div key={m._id || `material-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`material-row-${idx}`}>
            <div className="col-span-4">
              <Label className="text-xs">Description</Label>
              <Input list={`mat-catalog-${idx}`} value={m.description} onChange={(e) => {
                const desc = e.target.value;
                const match = catalogue.find(c => c.description === desc);
                if (match) updateLine('materials', idx, { description: desc, unit_cost: match.default_cost, markup_pct: match.default_markup_pct, unit_label: match.unit_label });
                else updateLine('materials', idx, { description: desc });
              }} data-testid={`material-desc-${idx}`} />
              <datalist id={`mat-catalog-${idx}`}>
                {catalogue.map(c => (<option key={c.id} value={c.description}>{c.description} (${c.default_cost})</option>))}
              </datalist>
            </div>
            <div className="col-span-2"><Label className="text-xs">Qty</Label><Input type="number" min={0} step={0.5} value={m.quantity} onChange={(e) => updateLine('materials', idx, { quantity: parseFloat(e.target.value || '0') })} data-testid={`material-qty-${idx}`} /></div>
            <div className="col-span-2"><Label className="text-xs">Unit cost</Label><Input type="number" min={0} step={0.01} value={m.unit_cost} onChange={(e) => updateLine('materials', idx, { unit_cost: parseFloat(e.target.value || '0') })} data-testid={`material-cost-${idx}`} /></div>
            <div className="col-span-2"><Label className="text-xs">Markup %</Label><Input type="number" min={0} step={1} value={m.markup_pct ?? ''} placeholder="inherit" onChange={(e) => updateLine('materials', idx, { markup_pct: e.target.value === '' ? null : parseFloat(e.target.value) })} data-testid={`material-markup-${idx}`} /></div>
            <div className="col-span-1"><Label className="text-xs">Unit</Label><Input value={m.unit_label || 'ea'} onChange={(e) => updateLine('materials', idx, { unit_label: e.target.value })} data-testid={`material-unit-${idx}`} /></div>
            <div className="col-span-1 flex justify-end">
              <Button size="icon" variant="ghost" onClick={() => removeLine('materials', idx)} data-testid={`material-remove-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {customRow('materials', idx, m)}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addMaterial} disabled={readOnly} data-testid="add-material-btn"><Plus className="w-4 h-4 mr-1" /> Add material</Button>
      </SectionCard>

      {/* ─── Vehicle ───────────────────────────────────────────────────── */}
      <SectionCard title="Vehicle / mileage" icon={Truck} description={`Per-${vehicleUnit} or one-off truck-roll fee. Defaults configured in Settings → Estimates.`} testId="section-vehicle">
        {(data.vehicle || []).map((v, idx) => (
          <div key={v._id || `vehicle-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`vehicle-row-${idx}`}>
            <div className="col-span-3">
              <Label className="text-xs">Mode</Label>
              <Select value={v.mode || 'distance'} onValueChange={(val) => updateLine('vehicle', idx, { mode: val })}>
                <SelectTrigger data-testid={`vehicle-mode-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="distance">Per {vehicleUnit}</SelectItem>
                  <SelectItem value="flat">One-off fee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {v.mode === 'distance' ? (
              <>
                <div className="col-span-2"><Label className="text-xs">Distance ({v.distance_unit || vehicleUnit})</Label><Input type="number" min={0} value={v.distance} onChange={(e) => updateLine('vehicle', idx, { distance: parseFloat(e.target.value || '0') })} data-testid={`vehicle-distance-${idx}`} /></div>
                <div className="col-span-2"><Label className="text-xs">Rate/{v.distance_unit || vehicleUnit}</Label><Input type="number" min={0} step={0.05} value={v.rate_per_unit} onChange={(e) => updateLine('vehicle', idx, { rate_per_unit: parseFloat(e.target.value || '0') })} data-testid={`vehicle-rate-${idx}`} /></div>
              </>
            ) : (
              <div className="col-span-4"><Label className="text-xs">Flat amount</Label><Input type="number" min={0} step={5} value={v.flat_amount} onChange={(e) => updateLine('vehicle', idx, { flat_amount: parseFloat(e.target.value || '0') })} data-testid={`vehicle-flat-${idx}`} /></div>
            )}
            <div className="col-span-4"><Label className="text-xs">Notes</Label><Input value={v.description || ''} onChange={(e) => updateLine('vehicle', idx, { description: e.target.value })} placeholder="e.g. Travel to/from site" data-testid={`vehicle-notes-${idx}`} /></div>
            <div className="col-span-1 flex justify-end">
              <Button size="icon" variant="ghost" onClick={() => removeLine('vehicle', idx)} data-testid={`vehicle-remove-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {customRow('vehicle', idx, v)}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addVehicle} disabled={readOnly} data-testid="add-vehicle-btn"><Plus className="w-4 h-4 mr-1" /> Add vehicle line</Button>
      </SectionCard>

      {/* ─── Fuel surcharge ────────────────────────────────────────────── */}
      <SectionCard title="Fuel / energy surcharge" icon={Fuel} description="Flat amount or % of labour. Useful for after-hours, hot-weather, or fuel-price volatility." testId="section-fuel">
        {(data.fuel || []).map((f, idx) => (
          <div key={f._id || `fuel-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`fuel-row-${idx}`}>
            <div className="col-span-3">
              <Label className="text-xs">Mode</Label>
              <Select value={f.mode || 'percent_of_labour'} onValueChange={(val) => updateLine('fuel', idx, { mode: val })}>
                <SelectTrigger data-testid={`fuel-mode-${idx}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent_of_labour">% of labour</SelectItem>
                  <SelectItem value="flat">Flat $</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.mode === 'percent_of_labour' ? (
              <div className="col-span-3"><Label className="text-xs">Percent</Label><Input type="number" min={0} step={0.5} value={f.percent} onChange={(e) => updateLine('fuel', idx, { percent: parseFloat(e.target.value || '0') })} data-testid={`fuel-pct-${idx}`} /></div>
            ) : (
              <div className="col-span-3"><Label className="text-xs">Amount</Label><Input type="number" min={0} step={1} value={f.flat_amount} onChange={(e) => updateLine('fuel', idx, { flat_amount: parseFloat(e.target.value || '0') })} data-testid={`fuel-flat-${idx}`} /></div>
            )}
            <div className="col-span-5"><Label className="text-xs">Notes</Label><Input value={f.description || ''} onChange={(e) => updateLine('fuel', idx, { description: e.target.value })} data-testid={`fuel-notes-${idx}`} /></div>
            <div className="col-span-1 flex justify-end">
              <Button size="icon" variant="ghost" onClick={() => removeLine('fuel', idx)} data-testid={`fuel-remove-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {customRow('fuel', idx, f)}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addFuel} disabled={readOnly} data-testid="add-fuel-btn"><Plus className="w-4 h-4 mr-1" /> Add fuel / energy line</Button>
      </SectionCard>

      {/* ─── Equipment ─────────────────────────────────────────────────── */}
      <SectionCard title="Equipment rental" icon={Wrench} testId="section-equipment">
        {(data.equipment || []).map((e, idx) => (
          <div key={e._id || `equipment-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`equipment-row-${idx}`}>
            <div className="col-span-5"><Label className="text-xs">Description</Label><Input value={e.description} onChange={(ev) => updateLine('equipment', idx, { description: ev.target.value })} placeholder="e.g. Drywall lift" data-testid={`equipment-desc-${idx}`} /></div>
            <div className="col-span-2"><Label className="text-xs">Days</Label><Input type="number" min={0} step={0.5} value={e.days} onChange={(ev) => updateLine('equipment', idx, { days: parseFloat(ev.target.value || '0') })} data-testid={`equipment-days-${idx}`} /></div>
            <div className="col-span-3"><Label className="text-xs">Daily rate</Label><Input type="number" min={0} step={1} value={e.daily_rate} onChange={(ev) => updateLine('equipment', idx, { daily_rate: parseFloat(ev.target.value || '0') })} data-testid={`equipment-rate-${idx}`} /></div>
            <div className="col-span-1 text-right text-xs text-slate-400 self-center">{fmtMoney(Number(e.days || 0) * Number(e.daily_rate || 0))}</div>
            <div className="col-span-1 flex justify-end">
              <Button size="icon" variant="ghost" onClick={() => removeLine('equipment', idx)} data-testid={`equipment-remove-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {customRow('equipment', idx, e)}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addEquipment} disabled={readOnly} data-testid="add-equipment-btn"><Plus className="w-4 h-4 mr-1" /> Add equipment line</Button>
      </SectionCard>

      {/* ─── Disposal ──────────────────────────────────────────────────── */}
      <SectionCard title="Disposal / dump fees" icon={Recycle} testId="section-disposal">
        {(data.disposal || []).map((d, idx) => (
          <div key={d._id || `disposal-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`disposal-row-${idx}`}>
            <div className="col-span-8"><Label className="text-xs">Description</Label><Input value={d.description} onChange={(e) => updateLine('disposal', idx, { description: e.target.value })} placeholder="e.g. Landfill tipping fee" data-testid={`disposal-desc-${idx}`} /></div>
            <div className="col-span-3"><Label className="text-xs">Amount</Label><Input type="number" min={0} step={1} value={d.amount} onChange={(e) => updateLine('disposal', idx, { amount: parseFloat(e.target.value || '0') })} data-testid={`disposal-amount-${idx}`} /></div>
            <div className="col-span-1 flex justify-end">
              <Button size="icon" variant="ghost" onClick={() => removeLine('disposal', idx)} data-testid={`disposal-remove-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {customRow('disposal', idx, d)}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addDisposal} disabled={readOnly} data-testid="add-disposal-btn"><Plus className="w-4 h-4 mr-1" /> Add disposal line</Button>
      </SectionCard>

      {/* ─── Custom ────────────────────────────────────────────────────── */}
      <SectionCard title="Custom line items" icon={Receipt} description="Anything that doesn't fit a category — permits, security fees, callout, after-hours premium, etc." testId="section-custom">
        {(data.custom || []).map((c, idx) => (
          <div key={c._id || `custom-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`custom-row-${idx}`}>
            <div className="col-span-6"><Label className="text-xs">Description</Label><Input value={c.description} onChange={(e) => updateLine('custom', idx, { description: e.target.value })} data-testid={`custom-desc-${idx}`} /></div>
            <div className="col-span-2"><Label className="text-xs">Qty</Label><Input type="number" min={0} step={0.5} value={c.quantity} onChange={(e) => updateLine('custom', idx, { quantity: parseFloat(e.target.value || '0') })} data-testid={`custom-qty-${idx}`} /></div>
            <div className="col-span-3"><Label className="text-xs">Rate</Label><Input type="number" min={0} step={1} value={c.rate} onChange={(e) => updateLine('custom', idx, { rate: parseFloat(e.target.value || '0') })} data-testid={`custom-rate-${idx}`} /></div>
            <div className="col-span-1 flex justify-end">
              <Button size="icon" variant="ghost" onClick={() => removeLine('custom', idx)} data-testid={`custom-remove-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {customRow('custom', idx, c)}
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addCustom} disabled={readOnly} data-testid="add-custom-btn"><Plus className="w-4 h-4 mr-1" /> Add custom line</Button>
      </SectionCard>

      {/* ─── Tax + deposit ─────────────────────────────────────────────── */}
      <SectionCard title="Tax &amp; deposit" icon={Calculator} testId="section-tax-deposit">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Tax rate (%)</Label>
            <Input type="number" min={0} max={50} step={0.5} value={data.tax_rate_override ?? 0} onChange={(e) => setData(d => ({ ...d, tax_rate_override: parseFloat(e.target.value || '0') }))} data-testid="tax-rate-input" disabled={readOnly} />
            <p className="text-xs text-slate-500 mt-1">Multi-component presets (HST + GST + PST) are configured in Settings → Invoice Template.</p>
          </div>
          <div data-testid="estimate-presentation-block">
            <Label>Presentation to customer</Label>
            <select value={data.presentation} onChange={(e) => setData(d => ({ ...d, presentation: e.target.value }))} disabled={readOnly}
              className="w-full h-10 rounded-md border px-3 text-sm mt-1" style={{ background: '#1A2433', color: '#fff', borderColor: '#2D3748' }} data-testid="estimate-presentation-select">
              <option value="itemized">Itemized — customer sees every line</option>
              <option value="lump_sum">Lump sum — total + what's included only</option>
            </select>
            {data.presentation === 'lump_sum' && (
              <textarea value={data.summary_included} onChange={(e) => setData(d => ({ ...d, summary_included: e.target.value }))} disabled={readOnly} rows={3}
                placeholder="Includes labour, equipment, materials and energy costs for the service."
                className="w-full rounded-md border px-3 py-2 text-sm mt-2" style={{ background: '#1A2433', color: '#fff', borderColor: '#2D3748' }} data-testid="estimate-summary-input" />
            )}
            <p className="text-xs text-slate-500 mt-1">Lump sum hides the breakdown on the customer page and PDF; the converted invoice inherits it.</p>
          </div>
          <div>
            <Label>Currency</Label>
            <select value={data.currency} onChange={(e) => setData(d => ({ ...d, currency: e.target.value }))} disabled={readOnly}
              className="w-full h-10 rounded-md border px-3 text-sm mt-1" style={{ background: '#1A2433', color: '#fff', borderColor: '#2D3748' }} data-testid="estimate-currency-select">
              <option value="">Auto — from customer / job-site country</option>
              {['CAD', 'USD', 'GBP', 'EUR', 'AUD', 'NZD', 'MXN', 'DKK', 'SEK', 'NOK', 'CHF'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>Material markup override (%)</Label>
            <Input type="number" min={0} step={1} placeholder="Inherit company default" value={data.material_markup_pct ?? ''} onChange={(e) => setData(d => ({ ...d, material_markup_pct: e.target.value === '' ? null : parseFloat(e.target.value) }))} data-testid="material-markup-input" disabled={readOnly} />
          </div>
          {settings?.deposit_enabled && (
            <div>
              <Label className="flex items-center justify-between">Require deposit
                <Switch checked={!!data.deposit_required} onCheckedChange={(v) => setData(d => ({ ...d, deposit_required: v }))} data-testid="deposit-toggle" disabled={readOnly} />
              </Label>
              {data.deposit_required && (
                <Input type="number" min={0} max={100} step={0.5} className="mt-2" value={data.deposit_pct} onChange={(e) => setData(d => ({ ...d, deposit_pct: parseFloat(e.target.value || '0') }))} data-testid="deposit-pct-input" disabled={readOnly} />
              )}
              <p className="text-xs text-slate-500 mt-1">Threshold: {settings.deposit_required_above != null ? `mandatory above ${fmtMoney(settings.deposit_required_above)}` : 'optional'}.</p>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Internal notes (not shown to customer)" icon={FileText} testId="section-notes">
        <Textarea rows={3} value={data.internal_notes} onChange={(e) => setData(d => ({ ...d, internal_notes: e.target.value }))} data-testid="estimate-internal-notes" disabled={readOnly} />
      </SectionCard>

      {/* ─── Sticky totals footer ──────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 md:left-[68px] lg:left-[240px] z-30 backdrop-blur bg-slate-900/95 border-t border-slate-700 px-4 py-3" data-testid="totals-footer">
        <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-3 text-sm">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-slate-400">Subtotal <strong className="text-white ml-1" data-testid="footer-subtotal">{fmtMoney(totals.subtotal)}</strong></span>
            <span className="text-slate-400">Tax <strong className="text-white ml-1" data-testid="footer-tax">{fmtMoney(totals.tax_total)}</strong></span>
            <span className="text-emerald-400 font-bold">TOTAL <span data-testid="footer-grand-total">{fmtMoney(totals.grand_total)}</span></span>
            {data.deposit_required && (
              <span className="text-amber-300">Deposit <strong className="ml-1" data-testid="footer-deposit">{fmtMoney(totals.deposit_amount)}</strong></span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => handleSave()} disabled={saving || readOnly} variant="outline" size="sm" data-testid="footer-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}</Button>
            {canSend && !readOnly && (
              <Button onClick={handleSend} disabled={sending || saving} size="sm" data-testid="footer-send-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
